import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { AuthHeader } from 'components';
import ResetPassword from 'components/Auth/ResetPassword';
import validations from 'lib/Validations';
import routeConstants from 'routes/routeConstants';
import { PasswordConfirmIdentity } from 'screens';
import { clearRequest, selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { App, forgotPasswordRequest } from 'store/App';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode } from 'types';
import { formatDateForApi } from 'utils/date';

const useQuery = () => new URLSearchParams(useLocation().search);

const PasswordLookup: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showConfirmIdentity, setShowConfirmIdentity] = useState(false);
  const query = useQuery();

  // Query params passed with v1
  const v1Email = query.get('id');
  const integrationName = query.get('integrationName');

  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, App.FORGOT_PASSWORD_REQUEST)) as IApiErrorPayloadWithCode;
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, App.FORGOT_PASSWORD_REQUEST));

  const onNewForm = useCallback(() => {
    dispatch(clearRequest(App.FORGOT_PASSWORD_REQUEST));
  }, [dispatch]);

  const { errors, touched, isValid, dirty, values, handleSubmit, getFieldProps, setFieldValue } = useFormik({
    initialValues: {
      identity: v1Email || '',
      dateOfBirth: '',
    },
    onSubmit: () => {
      dispatch(forgotPasswordRequest({ ...values, dateOfBirth: formatDateForApi(values.dateOfBirth) }, onSuccess));
    },
    validateOnBlur: true,
    validationSchema: validations.forgotPasswordValidationSchema,
  });

  const onSuccess = () => {
    setShowConfirmIdentity(true);
  };

  const validateField = (field: 'identity' | 'dateOfBirth') => Boolean(touched[field] && errors[field]);

  const onBack = () => {
    setShowConfirmIdentity(false);
  };

  const authHeader = (
    <AuthHeader
      data-cy="auth-reset-password-header"
      title="Reset Password"
      subtitle="Enter the email you use to log in to reset your password."
      onBack={() => (history?.location?.key ? history.goBack() : history.push(routeConstants.public.rootPath))}
    />
  );

  if (showConfirmIdentity) {
    let confPayload = values;
    confPayload = {
      ...confPayload,
      ...(isEmpty(values.dateOfBirth) && { dateOfBirth: '01-01-1970' }),
    };
    const submitUrl = routeConstants.public.resetPasswordCreateNew + (integrationName ? `?integrationName=${integrationName}` : '');

    return <PasswordConfirmIdentity onBack={onBack} onSubmitUrl={submitUrl} resendPayload={confPayload} />;
  }

  return (
    <ResetPassword
      formId={App.FORGOT_PASSWORD_REQUEST}
      errors={errors}
      apiErrors={apiErrors}
      authHeader={authHeader}
      onNewForm={onNewForm}
      validateField={validateField}
      setFieldValue={setFieldValue}
      isLoading={isLoading}
      isValid={isValid}
      dirty={dirty}
      handleSubmit={handleSubmit}
      getFieldProps={getFieldProps}
    />
  );
};

export default PasswordLookup;
